import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { colors } from '../design/tokens'
import { informesApi } from '../api/informes'
import { remitosApi, notasEnvioApi } from '../api/remitos'
import api from '../api/client'
import { fmtFecha, toISOLocal } from '../utils/fecha'
import { copiarTexto } from '../utils/copiar'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import Boton from '../components/Boton'
import TiemposEntregaCard from '../components/TiemposEntregaCard'

const PRESETS = [
  { value: 'hoy',    label: 'Hoy',     dias: 0 },
  { value: '7dias',  label: '7 días',  dias: 6 },
  { value: '30dias', label: '30 días', dias: 29 },
  { value: '90dias', label: '90 días', dias: 89 },
]

const COLORES = {
  entregado:    '#16a34a',
  no_entregado: '#dc2626',
  reagendado:   '#9333ea',
  pendiente:    '#3b82f6',
}

const iso = (d) => toISOLocal(d)

// Número de documento en un solo renglón, seleccionable de un click + botón copiar
function CopiableDoc({ numero, tipo }) {
  const [copiado, setCopiado] = useState(false)
  const texto = numero || ''
  const copiar = async () => {
    if (!texto) return
    if (await copiarTexto(texto)) { setCopiado(true); setTimeout(() => setCopiado(false), 1500) }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <code style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: '0.8rem', userSelect: 'all' }}>{texto || '—'}</code>
      <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#5b21b6', background: '#ede9fe', padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>
        {tipo === 'nota_envio' ? 'NE' : 'Re'}
      </span>
      {texto && (
        <button onClick={copiar} title="Copiar número" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8rem', padding: 0, lineHeight: 1, color: copiado ? '#16a34a' : '#94a3b8' }}>
          {copiado ? '✓' : '📋'}
        </button>
      )}
    </div>
  )
}
const hace = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); return d }

// ── Dona SVG de resultados ────────────────────────────────────────────────
function Dona({ entregados, noEntregados, reagendados, pendientes }) {
  const total = entregados + noEntregados + reagendados + pendientes
  const segs = [
    { v: entregados,   c: COLORES.entregado },
    { v: reagendados,  c: COLORES.reagendado },
    { v: pendientes,   c: COLORES.pendiente },
    { v: noEntregados, c: COLORES.no_entregado },
  ].filter(s => s.v > 0)

  const R = 60, SW = 22, C = 2 * Math.PI * R
  let offset = 0
  const tasa = total > 0 ? Math.round((entregados / total) * 100) : 0

  return (
    <svg viewBox="0 0 160 160" style={{ width: 160, height: 160 }}>
      <circle cx="80" cy="80" r={R} fill="none" stroke="#f1f5f9" strokeWidth={SW} />
      {total > 0 && segs.map((s, i) => {
        const len = (s.v / total) * C
        const el = (
          <circle key={i} cx="80" cy="80" r={R} fill="none" stroke={s.c} strokeWidth={SW}
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset}
            transform="rotate(-90 80 80)" />
        )
        offset += len
        return el
      })}
      <text x="80" y="74" textAnchor="middle" fontSize="26" fontWeight="800" fill="#0f172a">{tasa}%</text>
      <text x="80" y="94" textAnchor="middle" fontSize="11" fill="#64748b">entregado</text>
    </svg>
  )
}

// ── Barras de tendencia diaria (SVG) ──────────────────────────────────────
function Tendencia({ data }) {
  if (!data?.length) return <p style={st.empty}>Sin datos en el período.</p>
  const W = Math.max(data.length * 34, 200), H = 150, pad = 20
  const maxV = Math.max(...data.map(d => Number(d.total)), 1)
  const bw = Math.min(26, (W - pad) / data.length - 6)

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H + 28}`} style={{ width: '100%', minWidth: W, height: H + 28 }}>
        {data.map((d, i) => {
          const x = pad + i * ((W - pad) / data.length)
          const ent = Number(d.entregados), no = Number(d.no_entregados)
          const tot = Number(d.total)
          const hEnt = (ent / maxV) * H, hNo = (no / maxV) * H
          const otros = tot - ent - no
          const hOtros = (otros / maxV) * H
          let y = H
          return (
            <g key={i}>
              {/* entregados (verde, base) */}
              <rect x={x} y={y - hEnt} width={bw} height={hEnt} fill={COLORES.entregado} rx="2" />
              {/* no entregados (rojo) */}
              <rect x={x} y={y - hEnt - hNo} width={bw} height={hNo} fill={COLORES.no_entregado} rx="2" />
              {/* otros (gris) */}
              {otros > 0 && <rect x={x} y={y - hEnt - hNo - hOtros} width={bw} height={hOtros} fill="#cbd5e1" rx="2" />}
              <text x={x + bw / 2} y={H + 12} textAnchor="middle" fontSize="9" fill="#94a3b8">
                {String(d.fecha).slice(8, 10)}/{String(d.fecha).slice(5, 7)}
              </text>
              {tot > 0 && <text x={x + bw / 2} y={y - hEnt - hNo - hOtros - 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#475569">{tot}</text>}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ── Barra horizontal simple ───────────────────────────────────────────────
function BarraH({ label, valor, max, sub, color = '#1e40af' }) {
  const pct = max > 0 ? (valor / max) * 100 : 0
  return (
    <div style={st.barraRow}>
      <div style={st.barraLabel} title={label}>{label}</div>
      <div style={st.barraTrack}>
        <div style={{ ...st.barraFill, width: `${pct}%`, background: color }} />
      </div>
      <div style={st.barraValor}>{sub ?? valor}</div>
    </div>
  )
}

export default function InformesPage() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [tiempos, setTiempos] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [fleteros, setFleteros] = useState([])
  const [preset, setPreset] = useState('30dias')
  const [desde, setDesde] = useState(iso(hace(29)))
  const [hasta, setHasta] = useState(iso(new Date()))
  const [fleteroId, setFleteroId] = useState('')
  const [liberandoId, setLiberandoId] = useState(null)
  const [accionMsg, setAccionMsg] = useState('')

  useEffect(() => {
    api.get('/fleteros').then(({ data }) => setFleteros(data.data || data || [])).catch(() => {})
  }, [])

  const aplicarPreset = (p) => {
    setPreset(p.value)
    setDesde(iso(hace(p.dias)))
    setHasta(iso(new Date()))
  }

  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) { setLoading(true); setError('') }
    try {
      const params = {
        fecha_desde: desde, fecha_hasta: hasta,
        ...(fleteroId ? { fletero_id: fleteroId } : {}),
      }
      const [{ data }, t] = await Promise.all([
        informesApi.entregas(params),
        // Si falla el de tiempos, el resto del tablero se muestra igual
        informesApi.tiemposEntrega(params).catch(() => null),
      ])
      setData(data)
      setTiempos(t?.data ?? null)
    } catch {
      if (!silent) setError('No se pudo cargar el informe.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [desde, hasta, fleteroId])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  // Libera el documento (no_entregado/reagendado/asignado) para reasignarlo a
  // una nueva salida. Cierra el agujero del reagendo "muerto": desde el Tablero
  // se puede accionar sin tener que entrar al detalle.
  const liberar = async (d) => {
    if (!window.confirm(`¿Liberar ${d.numero} para reasignarlo a una nueva salida?`)) return
    setLiberandoId(`${d.entidad_tipo}-${d.id}`); setAccionMsg(null)
    try {
      if (d.entidad_tipo === 'nota_envio') await notasEnvioApi.liberar(d.entidad_id)
      else await remitosApi.liberar(d.entidad_id)
      setAccionMsg({ ok: true, texto: `${d.numero} liberado — ya está disponible en Asignación para armar una nueva salida.` })
      await cargar({ silent: true })
    } catch (err) {
      setAccionMsg({ ok: false, texto: err.response?.data?.message || 'No se pudo liberar el documento.' })
    } finally { setLiberandoId(null) }
  }

  const r = data?.resumen
  const maxFletero = Math.max(...(data?.por_fletero || []).map(f => Number(f.total)), 1)
  const maxZona = Math.max(...(data?.por_zona || []).map(z => Number(z.total)), 1)
  const maxMotivo = Math.max(...(data?.por_motivo || []).map(m => Number(m.n)), 1)

  return (
    <div style={st.wrap}>
      <div style={st.headerRow}>
        <div>
          <h1 style={st.h1}>📊 Tablero de entregas</h1>
          <p style={st.subtitle}>Situación general de la distribución</p>
        </div>
      </div>

      {/* Filtros */}
      <div style={st.filtros}>
        <div style={st.presetGroup}>
          {PRESETS.map(p => (
            <button key={p.value} onClick={() => aplicarPreset(p)}
              style={{ ...st.presetBtn, ...(preset === p.value ? st.presetBtnActive : {}) }}>
              {p.label}
            </button>
          ))}
        </div>
        <input type="date" value={desde} onChange={e => { setDesde(e.target.value); setPreset('') }} style={st.input} />
        <span style={{ color: '#94a3b8' }}>→</span>
        <input type="date" value={hasta} onChange={e => { setHasta(e.target.value); setPreset('') }} style={st.input} />
        <select value={fleteroId} onChange={e => setFleteroId(e.target.value)} style={st.input}>
          <option value="">Todos los fleteros</option>
          {fleteros.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
        </select>
      </div>

      {loading ? (
        <p style={{ color: '#94a3b8' }}>Cargando informe...</p>
      ) : error ? (
        <div style={st.errorBox}>{error}</div>
      ) : !data || r.total === 0 ? (
        <div style={st.vacio}>
          <p style={{ fontSize: '2.5rem', margin: 0 }}>📭</p>
          <p>No hay entregas registradas en este período.</p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div style={st.kpiGrid}>
            <div style={st.kpiCard}>
              <div style={st.kpiLabel}>Documentos</div>
              <div style={st.kpiNum}>{r.total}</div>
              <div style={st.kpiSub}>{data.salidas.total} salidas</div>
            </div>
            <div style={{ ...st.kpiCard, borderTopColor: COLORES.entregado }}>
              <div style={st.kpiLabel}>Entregados</div>
              <div style={{ ...st.kpiNum, color: COLORES.entregado }}>{r.entregados}</div>
              <div style={st.kpiSub}>tasa de éxito {r.tasa_exito}%</div>
            </div>
            <div style={{ ...st.kpiCard, borderTopColor: COLORES.no_entregado }}>
              <div style={st.kpiLabel}>No entregados</div>
              <div style={{ ...st.kpiNum, color: COLORES.no_entregado }}>
                {r.accionables ?? 0}<span style={st.kpiFrac}>/{r.no_entregados}</span>
              </div>
              <div style={st.kpiSub}>{r.accionables ?? 0} accionables · {r.en_reparto ?? 0} en proceso · {r.recuperados ?? 0} recuperadas</div>
            </div>
            <div style={{ ...st.kpiCard, borderTopColor: COLORES.reagendado }}>
              <div style={st.kpiLabel}>Reagendados</div>
              <div style={{ ...st.kpiNum, color: COLORES.reagendado }}>{r.reagendados}</div>
              <div style={st.kpiSub}>reprogramados</div>
            </div>
            <div style={{ ...st.kpiCard, borderTopColor: COLORES.pendiente }}>
              <div style={st.kpiLabel}>Pendientes</div>
              <div style={{ ...st.kpiNum, color: COLORES.pendiente }}>{r.pendientes}</div>
              <div style={st.kpiSub}>en reparto</div>
            </div>
          </div>

          {/* Fila: dona + tendencia */}
          <div style={st.row2}>
            <div style={{ ...st.card, ...st.donaCard }}>
              <h3 style={st.cardTitle}>Resultado global</h3>
              <Dona entregados={r.entregados} noEntregados={r.no_entregados} reagendados={r.reagendados} pendientes={r.pendientes} />
              <div style={st.leyenda}>
                <span style={st.leyItem}><i style={{ background: COLORES.entregado }} />Entregado ({r.entregados})</span>
                <span style={st.leyItem}><i style={{ background: COLORES.no_entregado }} />No entregado ({r.no_entregados})</span>
                {r.reagendados > 0 && <span style={st.leyItem}><i style={{ background: COLORES.reagendado }} />Reagendado ({r.reagendados})</span>}
                {r.pendientes > 0 && <span style={st.leyItem}><i style={{ background: COLORES.pendiente }} />Pendiente ({r.pendientes})</span>}
              </div>
            </div>
            <div style={{ ...st.card, flex: 1 }}>
              <h3 style={st.cardTitle}>Tendencia diaria</h3>
              <Tendencia data={data.tendencia} />
              <div style={st.leyenda}>
                <span style={st.leyItem}><i style={{ background: COLORES.entregado }} />Entregados</span>
                <span style={st.leyItem}><i style={{ background: COLORES.no_entregado }} />No entregados</span>
                <span style={st.leyItem}><i style={{ background: '#cbd5e1' }} />Otros</span>
              </div>
            </div>
          </div>

          <TiemposEntregaCard data={tiempos} />

          {/* Ranking de fleteros */}
          <div style={st.card}>
            <h3 style={st.cardTitle}>Rendimiento por fletero</h3>
            <table style={st.tabla}>
              <thead>
                <tr>
                  <th style={st.th}>Fletero</th>
                  <th style={st.thC}>Total</th>
                  <th style={st.thC}>✓</th>
                  <th style={st.thC}>✕</th>
                  <th style={st.thC}>↻</th>
                  <th style={{ ...st.th, width: '30%' }}>Tasa de éxito</th>
                </tr>
              </thead>
              <tbody>
                {data.por_fletero.map(f => (
                  <tr key={f.id} style={st.tr}>
                    <td style={st.td}><strong>{f.nombre}</strong></td>
                    <td style={st.tdC}>{f.total}</td>
                    <td style={{ ...st.tdC, color: COLORES.entregado, fontWeight: 700 }}>{f.entregados}</td>
                    <td style={{ ...st.tdC, color: COLORES.no_entregado, fontWeight: 700 }}>{f.no_entregados}</td>
                    <td style={{ ...st.tdC, color: COLORES.reagendado }}>{f.reagendados}</td>
                    <td style={st.td}>
                      <div style={st.tasaWrap}>
                        <div style={st.barraTrack}>
                          <div style={{ ...st.barraFill, width: `${f.tasa_exito}%`, background: f.tasa_exito >= 80 ? COLORES.entregado : f.tasa_exito >= 50 ? '#f59e0b' : COLORES.no_entregado }} />
                        </div>
                        <span style={st.tasaNum}>{f.tasa_exito}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Fila: zonas + motivos */}
          <div style={st.row2}>
            <div style={{ ...st.card, flex: 1 }}>
              <h3 style={st.cardTitle}>Entregas por zona</h3>
              {data.por_zona.length === 0 ? <p style={st.empty}>Sin datos.</p> :
                data.por_zona.map(z => (
                  <BarraH key={z.zona} label={z.zona} valor={Number(z.total)} max={maxZona}
                    sub={`${z.entregados}/${z.total}`}
                    color={Number(z.no_entregados) > 0 ? '#f59e0b' : COLORES.entregado} />
                ))}
            </div>
            <div style={{ ...st.card, flex: 1 }}>
              <h3 style={st.cardTitle}>Motivos de no entrega</h3>
              {data.por_motivo.length === 0 ? <p style={st.empty}>Sin no-entregas 🎉</p> :
                data.por_motivo.map(m => (
                  <BarraH key={m.motivo} label={m.motivo} valor={Number(m.n)} max={maxMotivo}
                    color={COLORES.no_entregado} />
                ))}
            </div>
          </div>

          {/* Detalle de no entregados + reagendados — seguimiento */}
          {data.detalle_no_entregados?.length > 0 && (
            <div style={st.card}>
              <h3 style={st.cardTitle}>
                Seguimiento de no entregados y reagendados ({data.detalle_no_entregados.length})
              </h3>
              {accionMsg && <div style={{ ...st.accionMsg, ...(accionMsg.ok ? {} : st.accionMsgError) }}>{accionMsg.texto}</div>}
              <div style={{ overflowX: 'auto' }}>
                <table style={st.tabla}>
                  <thead>
                    <tr>
                      <th style={st.th}>Documento</th>
                      <th style={st.th}>Cliente / Destinatario</th>
                      <th style={st.th}>Dirección</th>
                      <th style={st.th}>Zona</th>
                      <th style={st.th}>Fletero</th>
                      <th style={st.th}>Fecha</th>
                      <th style={st.th}>Motivo</th>
                      <th style={st.thC}>Estado</th>
                      <th style={st.thC}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Orden: lo que requiere acción arriba; recuperadas al final */}
                    {[...data.detalle_no_entregados]
                      .sort((a, b) => (SEGUI[a.estado_seguimiento]?.peso ?? 9) - (SEGUI[b.estado_seguimiento]?.peso ?? 9))
                      .map(d => {
                        // OJO: d.id es el id del salida_documento; el id real
                        // del NE/remito viene en d.entidad_id.
                        // Como esta vista es logística (Tablero), linkamos al
                        // detalle dentro del módulo.
                        const ruta = d.entidad_tipo === 'nota_envio'
                          ? `/logistica/notas-envio/${d.entidad_id}`
                          : `/logistica/remitos/${d.entidad_id}`
                        return (
                          <tr key={`${d.entidad_tipo}-${d.id}`} style={st.tr}>
                            <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                              <CopiableDoc numero={d.numero} tipo={d.entidad_tipo} />
                            </td>
                            <td style={st.td}>
                              <div style={{ fontWeight: 600 }}>{d.cliente || '—'}</div>
                              {d.telefono && <div style={st.tel}>📞 {d.telefono}</div>}
                            </td>
                            <td style={{ ...st.td, maxWidth: 200, fontSize: '0.8rem', color: '#64748b' }}>{d.direccion || '—'}</td>
                            <td style={st.td}>{d.zona || <span style={st.muted}>Sin zona</span>}</td>
                            <td style={st.td}>{d.fletero}</td>
                            <td style={st.td}>{fmtFecha(d.fecha)}</td>
                            <td style={st.td}><span style={st.motivoChip}>{d.motivo}</span>{d.notas && <div style={st.notas}>{d.notas}</div>}</td>
                            <td style={st.tdC}>
                              {(() => { const s = SEGUI[d.estado_seguimiento] || SEGUI.no_entregada
                                return <span style={{ ...st.pillBase, color: s.color, background: s.bg }}>{s.icon} {s.label}</span> })()}
                            </td>
                            <td style={st.tdC}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                {/* Acción directa: liberar lo que requiere acción
                                    (sin reasignar / no entregada / reagendado vivo). */}
                                {['no_entregada', 'sin_reasignar'].includes(d.estado_seguimiento) && (
                                  <Boton variante="secondary" sm
                                    disabled={liberandoId === `${d.entidad_tipo}-${d.id}`}
                                    onClick={() => liberar(d)}
                                    title="Liberar para reasignar a una nueva salida">
                                    {liberandoId === `${d.entidad_tipo}-${d.id}` ? '…' : '↩ Liberar'}
                                  </Boton>
                                )}
                                <Boton variante="secondary" sm onClick={() => navigate(ruta)}>Ver →</Boton>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
              <p style={st.leyendaTabla}>
                <b>No entregada</b>: falló o se reagendó y nadie la tocó (botón <b>↩ Liberar</b> para reasignar). <b>Sin reasignar</b>: liberada, esperando nueva salida. <b>Asignada</b>: en una salida en armado. <b>En distribución</b>: viajando en el segundo intento. <b>Recuperada</b>: se reentregó.
              </p>
            </div>
          )}

          {/* Las encuestas se mudaron a CLIENTES → Reseñas (28 ago 2026): bandeja
              con aviso a quien procesó, "Atendida", y los mismos resultados. */}
          <p style={{ margin: '8px 0 16px', fontSize: 13, color: colors.textSub }}>
            Conformidad (encuestas de entrega, compra y eventos) se mudó a <Link to="/clientes/resenas" style={{ color: colors.link, fontWeight: 600 }}>Reseñas de clientes →</Link>
          </p>

          <p style={st.footer}>
            Período: {fmtFecha(data.rango.desde)} → {fmtFecha(data.rango.hasta)} · Excluye salidas en armado
          </p>
        </>
      )}
    </div>
  )
}

const st = {
  wrap: { maxWidth: 1100, margin: '0 auto', paddingBottom: '3rem' },
  headerRow: { marginBottom: '1.25rem' },
  h1: { fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: 0 },
  subtitle: { margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.875rem' },

  filtros: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', background: '#fff', padding: '0.75rem', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  presetGroup: { display: 'flex', gap: 4 },
  presetBtn: { padding: '6px 13px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', color: '#475569' },
  presetBtnActive: { background: '#1e40af', color: '#fff', border: '1px solid #1e40af' },
  input: { padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.875rem' },

  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 8 },
  accionMsg: { background: '#ecfdf5', color: '#065f46', padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: '0.8rem', marginBottom: 10, fontWeight: 600 },
  accionMsgError: { background: '#fee2e2', color: '#991b1b' },
  vacio: { textAlign: 'center', padding: '60px 0', color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.875rem', marginBottom: '1rem' },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,.06)', borderTop: '3px solid #94a3b8' },
  kpiLabel: { fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  kpiNum: { fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, margin: '0.25rem 0' },
  kpiFrac: { fontSize: '1.1rem', fontWeight: 700, color: '#16a34a', marginLeft: 2 },
  kpiSub: { fontSize: '0.75rem', color: '#94a3b8' },

  row2: { display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' },
  card: { background: '#fff', borderRadius: 10, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,.06)', marginBottom: '1rem' },
  cardTitle: { margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' },
  donaCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 220 },

  leyenda: { display: 'flex', gap: '0.875rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '0.875rem' },
  leyItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#475569', fontWeight: 600 },

  empty: { color: '#94a3b8', fontSize: '0.875rem', margin: 0 },

  tabla: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #e2e8f0' },
  thC: { padding: '8px 10px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '9px 10px', fontSize: '0.875rem', color: '#1e293b' },
  tdC: { padding: '9px 10px', fontSize: '0.875rem', color: '#1e293b', textAlign: 'center' },
  tasaWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  tasaNum: { fontSize: '0.8125rem', fontWeight: 700, color: '#475569', minWidth: 38, textAlign: 'right' },

  barraRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  barraLabel: { width: 120, fontSize: '0.8125rem', color: '#475569', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 },
  barraTrack: { flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' },
  barraFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  barraValor: { width: 60, fontSize: '0.8125rem', fontWeight: 700, color: '#1e293b', textAlign: 'right', flexShrink: 0 },

  footer: { fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', marginTop: '0.5rem' },

  codigo: { fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' },
  tipoTag: { marginLeft: 6, fontSize: '0.6rem', fontWeight: 800, color: '#5b21b6', background: '#ede9fe', padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' },
  tel: { fontSize: '0.75rem', color: '#64748b', marginTop: 2 },
  muted: { color: '#cbd5e1', fontStyle: 'italic' },
  motivoChip: { fontSize: '0.75rem', fontWeight: 600, color: '#9a3412', background: '#ffedd5', padding: '2px 8px', borderRadius: 8, display: 'inline-block' },
  notas: { fontSize: '0.72rem', color: '#94a3b8', marginTop: 3, fontStyle: 'italic' },
  pillBase: { fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', display: 'inline-block' },
  leyendaTabla: { fontSize: '0.72rem', color: '#94a3b8', marginTop: 10, marginBottom: 0 },
}

// Máquina de estados del seguimiento de no entregados (orden = prioridad de acción).
const SEGUI = {
  no_entregada:    { peso: 0, label: 'No entregada',    icon: '✕', color: '#991b1b', bg: '#fee2e2' },
  sin_reasignar:   { peso: 1, label: 'Sin reasignar',   icon: '⚠', color: '#9a3412', bg: '#ffedd5' },
  asignada:        { peso: 2, label: 'Asignada',        icon: '📋', color: '#1e40af', bg: '#dbeafe' },
  en_distribucion: { peso: 3, label: 'En distribución', icon: '🚚', color: '#6d28d9', bg: '#ede9fe' },
  recuperada:      { peso: 4, label: 'Recuperada',      icon: '✓', color: '#166534', bg: '#dcfce7' },
}
